import React, {FC} from 'react';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {buildTeammateSearchUrl} from '../../../../helpers/models/teammateHelpers';
import {CompanyModel} from '../../../../models/api/companyModel';
import {RefinementOptionModel} from '../../../../models/api/refinementOptionModel';
import {ApiSearchTeammates} from '../../../../models/api/searchTeammateModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {fetchApiAsync} from '../../../../services/http/http';
import {RefinedInboxParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  CellsRefinementOptionsBaseSearchDropdown,
  REFINEMENT_SEARCH_DROPDOWN_PAGINATION_LIMIT,
  RefinementSearchDropdownPaginationState,
} from './cellsRefinementOptionsBaseSearchDropdown';
import {CellsRefinementOptionsSearchAssigneesDropdownRow} from './cellsRefinementOptionsSearchAssigneesDropdownRow';

/*
 * Props.
 */

interface CellsRefinementOptionsSearchAssigneesDropdownProps {
  company: CompanyModel;
  popoverProps: PopoverTransferProps;
  selectedRefinement: RefinementOptionModel | undefined;
  parameters: RefinedInboxParameters;
  onSelectAssignee: HandlerOf<TeammateModel>;
  onDropdownNavigateBack: Handler;
}

/*
 * Component.
 */

export const CellsRefinementOptionsSearchAssigneesDropdown: FC<
  CellsRefinementOptionsSearchAssigneesDropdownProps
> = (props) => {
  const {popoverProps, selectedRefinement, parameters, onDropdownNavigateBack, onSelectAssignee, company} =
    props;

  return (
    <CellsRefinementOptionsBaseSearchDropdown<TeammateModel | null>
      popoverProps={popoverProps}
      selectedRefinement={selectedRefinement}
      parameters={parameters}
      renderRow={(assignee) => renderAssigneesRow(selectedRefinement, onSelectAssignee, assignee)}
      fetchData={(...args) => fetchAssigneesData(company, ...args)}
      onDropdownNavigateBack={onDropdownNavigateBack}
    />
  );
};

function renderAssigneesRow(
  selectedRefinement: RefinementOptionModel | undefined,
  onSelectAssignee: HandlerOf<TeammateModel>,
  assignee: TeammateModel | null,
) {
  return (
    <CellsRefinementOptionsSearchAssigneesDropdownRow
      selectedRefinement={selectedRefinement}
      onSelectAssignee={onSelectAssignee}
      isNotInteractive={false}
      assignee={assignee}
    />
  );
}

async function fetchAssigneesData(
  company: CompanyModel,
  paginationState: RefinementSearchDropdownPaginationState,
  teammates: ReadonlyArray<TeammateModel | null>,
  query: string,
  cancelToken: CancelToken,
): Promise<{
  data: ReadonlyArray<TeammateModel | null>;
  canRequestMore: boolean;
}> {
  const url = buildTeammateSearchUrl(company.url, query, {
    offset: paginationState.offset,
    limit: REFINEMENT_SEARCH_DROPDOWN_PAGINATION_LIMIT,
  });

  const teammateSearch = await fetchApiAsync(ApiSearchTeammates, url, {cancelToken});

  const previousAndCurrentAssignees = [...teammates, ...teammateSearch.teammates];
  const shouldShowDropdownCategory = paginationState.offset === 0 && previousAndCurrentAssignees.length > 0;
  const data = [...(shouldShowDropdownCategory ? [null] : []), ...previousAndCurrentAssignees];

  return {
    data,
    canRequestMore: teammateSearch.teammates.length === teammateSearch.limit,
  };
}
